import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  FileText,
  Users,
  Upload,
  UserPlus,
  UserCheck,
  KeyRound,
  TriangleAlert,
  User,
} from 'lucide-react';
import { MigratedStudent, StudentsApi } from '@/services/school.api';
import { AcademicClassesApi, AcademicYearsApi, isNoCurrentYearError } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportButtons } from '@/components/ui/ExportButtons';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import {
  ImportStudentsModal,
  BulkAssignModal,
} from './StudentBulkModals';
import { PortalPinModal } from '@/components/shared/PortalPinModal';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';

const columnHelper = createColumnHelper<MigratedStudent>();

export function StudentsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const term = useTerminology();
  const { can } = usePermissions();
  const canCreate = can('/students', 'create');
  const canDelete = can('/students', 'delete');
  const canWrite = canCreate || canDelete;

  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'admissionNo', defaultSortOrder: 'asc' });
  const [yearId, setYearId] = useState('');
  const [unassigned, setUnassigned] = useState(false);
  const [includeLeft, setIncludeLeft] = useState(false);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [admissionYear, setAdmissionYear] = useState('');

  const { data: admissionYears } = useQuery({
    queryKey: ['students', 'admission-years'],
    queryFn: StudentsApi.admissionYears,
  });

  // Full year list for the selector — separate from "which year is current"
  // (that's a distinct question, handled below via AcademicYearsApi.current).
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  // Classes already come back with their sections embedded, so the section
  // dropdown needs no separate API call — just the selected class's list.
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const {
    data: currentYear,
    error: currentYearError,
    isLoading: currentYearLoading,
  } = useQuery({
    queryKey: ['academic-years', 'current'],
    queryFn: AcademicYearsApi.current,
    retry: (failureCount, err) => !isNoCurrentYearError(err) && failureCount < 3,
  });
  const noCurrentYear = isNoCurrentYearError(currentYearError);
  const effectiveYearId = yearId || currentYear?.id || '';
  // Only when nothing has been picked AND the school has no current year —
  // the selector stays usable so an explicit year can still be chosen.
  const showNoCurrentYearState = !currentYearLoading && noCurrentYear && !yearId;

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['students', dt.params, effectiveYearId, unassigned, includeLeft, classId, sectionId, admissionYear],
    queryFn: () =>
      StudentsApi.list({
        ...dt.params,
        academicYearId: effectiveYearId,
        unassigned: unassigned || undefined,
        includeLeft: includeLeft || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        admissionYear: admissionYear ? Number(admissionYear) : undefined,
      }),
    enabled: !!effectiveYearId,
    placeholderData: keepPreviousData,
  });

  const [confirm, setConfirm] = useState<{ open: boolean; student?: MigratedStudent }>(
    { open: false },
  );
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pinModal, setPinModal] = useState<{ open: boolean; student?: MigratedStudent }>(
    { open: false },
  );

  const setPin = useMutation({
    mutationFn: (v: { id: string; pin: string }) =>
      StudentsApi.setPin(v.id, v.pin),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      setPinModal({ open: false });
    },
  });
  const removePin = useMutation({
    mutationFn: (id: string) => StudentsApi.removePin(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      setPinModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => StudentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['school-stats'] });
      setConfirm({ open: false });
    },
  });

  const columns = [
    columnHelper.accessor('admissionNo', {
      header: 'Adm #',
      enableSorting: true,
      cell: (info) => (
        <code className="text-xs font-medium text-slate-700">{info.getValue()}</code>
      ),
    }),
    columnHelper.display({
      id: 'student',
      header: 'Student Info',
      cell: (info) => {
        const s = info.row.original;
        return (
          <div className="flex items-start gap-2.5">
            {s.photo ? (
              <img
                src={s.photo}
                alt=""
                className="h-9 w-9 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <User className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0">
              {s.nameMissing ? (
                <span
                  className="inline-flex items-center gap-1.5 text-amber-700"
                  title="This student's account link is broken in the legacy data — name is unavailable."
                >
                  <TriangleAlert className="h-4 w-4 shrink-0" />
                  (name missing)
                </span>
              ) : (
                <button
                  className="font-medium text-slate-900 hover:text-brand-600 hover:underline"
                  onClick={() => navigate(`/students/${s.id}`)}
                  title="View profile"
                >
                  {s.name}
                </button>
              )}
              <div className="text-xs text-slate-500">
                Roll #: {s.rollNumber ?? '—'}
                <br />
                DOB: {fmtDate(s.dob)}
                {s.gender ? ` · ${capitalize(s.gender)}` : ''}
              </div>
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'class',
      header: `${term.level} · ${term.group}`,
      cell: (info) => {
        const s = info.row.original;
        return s.enrolled ? (
          <span className="text-sm">
            <Badge tone="blue">{s.className ?? '—'}</Badge>{' '}
            <Badge tone="slate">{s.sectionName ?? '—'}</Badge>
          </span>
        ) : (
          <span className="text-slate-400">Not enrolled</span>
        );
      },
    }),
    columnHelper.display({
      id: 'academicDetails',
      header: 'Academic Details',
      cell: (info) => {
        const s = info.row.original;
        return (
          <div className="text-xs text-slate-600">
            <div>Username: {s.username ?? '—'}</div>
            <div>Admission Date: {fmtDate(s.admissionDate)}</div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'guardianDetails',
      header: 'Guardian Details',
      cell: (info) => {
        const g = info.row.original.guardian;
        if (!g) return <span className="text-slate-400">—</span>;
        return (
          <div className="text-xs text-slate-600">
            <div className="font-medium text-slate-800">{g.name || '—'}</div>
            <div>{g.mobile ?? g.username ?? '—'}</div>
            {g.gender ? <div>{capitalize(g.gender)}</div> : null}
          </div>
        );
      },
    }),
    columnHelper.accessor('address', {
      header: 'Address',
      enableSorting: false,
      cell: (info) => (
        <span className="line-clamp-2 max-w-xs text-xs text-slate-600">
          {info.getValue() || '—'}
        </span>
      ),
    }),
    columnHelper.accessor('enrollmentStatus', {
      header: 'Status',
      enableSorting: false,
      // Decoded from the legacy promote_students.status column comment
      // (1=>continue, 0=>leave) — null means no enrollment row for this
      // year at all (already visible via "Not enrolled" in Class · Section).
      cell: (info) => {
        const status = info.getValue();
        if (status === null) return <span className="text-slate-400">—</span>;
        return (
          <Badge tone={status === 'continue' ? 'green' : 'amber'}>
            {status === 'continue' ? 'Continuing' : 'Left'}
          </Badge>
        );
      },
    }),
    ...(canWrite
      ? [
          columnHelper.display({
            id: 'actions',
            header: '',
            cell: (info) => {
              const s = info.row.original;
              return (
                <div className="flex items-center justify-end gap-0.5">
                  {canCreate && (
                    <button
                      className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                      onClick={() => navigate(`/students/${s.id}/edit`)}
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                    onClick={() => navigate(`/parents?student=${s.id}`)}
                    title="Parents & guardians"
                  >
                    <Users className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-600"
                    onClick={() => navigate(`/visitors?student=${s.id}`)}
                    title="Visitors"
                  >
                    <UserCheck className="h-4 w-4" />
                  </button>
                  <button
                    className={`rounded-md p-1.5 hover:bg-slate-100 ${
                      s.userId ? 'text-green-600' : 'text-slate-500 hover:text-brand-600'
                    }`}
                    onClick={() => setPinModal({ open: true, student: s })}
                    title={s.userId ? 'Portal access enabled' : 'Set portal PIN'}
                  >
                    <KeyRound className="h-4 w-4" />
                  </button>
                  <button
                    className="rounded-md p-1.5 text-slate-500 hover:bg-amber-50 hover:text-amber-600"
                    onClick={() => navigate(`/transfer-certificates?student=${s.id}`)}
                    title="Issue Transfer Certificate"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                  {canDelete && (
                    <button
                      className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => setConfirm({ open: true, student: s })}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            },
          }),
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Students"
        description="Admission records and enrollments. Search by name or admission number."
        actions={
          <div className="flex items-center gap-2">
            <ExportButtons
              onExport={(format) =>
                StudentsApi.export(format, {
                  search: dt.params.search,
                  academicYearId: effectiveYearId || undefined,
                  classId: classId || undefined,
                  sectionId: sectionId || undefined,
                  admissionYear: admissionYear ? Number(admissionYear) : undefined,
                  includeLeft: includeLeft || undefined,
                  unassigned: unassigned || undefined,
                })
              }
            />
            {canCreate && (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => setBulkOpen(true)}
                  disabled={years.length === 0}
                  title={`Assign many students to a ${term.level.toLowerCase()}`}
                >
                  <UserPlus className="mr-1.5 h-4 w-4" /> Bulk assign
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setImportOpen(true)}
                  disabled={years.length === 0}
                  title="Import students from Excel"
                >
                  <Upload className="mr-1.5 h-4 w-4" /> Import
                </button>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/students/new')}
                  disabled={years.length === 0}
                  title={
                    years.length === 0
                      ? 'Create an academic year first'
                      : 'Enroll a new student'
                  }
                >
                  <Plus className="mr-1.5 h-4 w-4" /> Add student
                </button>
              </>
            )}
          </div>
        }
      />

      {showNoCurrentYearState ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          This school has no current academic year set. Pick a year below to view its students.
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select
          className="!w-44"
          value={yearId}
          onChange={(e) => setYearId(e.target.value)}
        >
          {!currentYear && !yearId && <option value="">Select a year…</option>}
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
              {y.isCurrent ? ' (current)' : ''}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
        >
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={admissionYear}
          onChange={(e) => setAdmissionYear(e.target.value)}
        >
          <option value="">All admission years</option>
          {(admissionYears ?? []).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={unassigned}
            onChange={(e) => setUnassigned(e.target.checked)}
            disabled={!effectiveYearId}
          />
          Unassigned only
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeLeft}
            onChange={(e) => setIncludeLeft(e.target.checked)}
            disabled={!effectiveYearId || unassigned}
          />
          Show inactive/left students
        </label>
      </div>

      {effectiveYearId ? (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage={
            unassigned
              ? 'No unassigned students for this year.'
              : dt.params.search
                ? 'No students match your search.'
                : 'No students enrolled for this year yet.'
          }
        />
      ) : null}

      <PortalPinModal
        open={pinModal.open}
        onClose={() => setPinModal({ open: false })}
        subject={pinModal.student?.nameMissing ? '(name missing)' : pinModal.student?.name ?? ''}
        loginHint={`Logs in with admission # ${pinModal.student?.admissionNo ?? ''}`}
        hasAccess={!!pinModal.student?.userId}
        busy={setPin.isPending || removePin.isPending}
        error={errMsg(setPin.error)}
        onSet={(pin) =>
          pinModal.student && setPin.mutate({ id: pinModal.student.id, pin })
        }
        onRevoke={() =>
          pinModal.student && removePin.mutate(pinModal.student.id)
        }
      />

      <ImportStudentsModal
        open={importOpen}
        years={years.map((y) => ({ id: y.id, name: y.name, isCurrent: y.isCurrent }))}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          qc.invalidateQueries({ queryKey: ['students'] });
          qc.invalidateQueries({ queryKey: ['school-stats'] });
        }}
      />

      <BulkAssignModal
        open={bulkOpen}
        years={years.map((y) => ({ id: y.id, name: y.name, isCurrent: y.isCurrent }))}
        onClose={() => setBulkOpen(false)}
        onAssigned={() => qc.invalidateQueries({ queryKey: ['students'] })}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.student && remove.mutate(confirm.student.id)}
        loading={remove.isPending}
        title="Delete student?"
        message={`Soft-delete ${confirm.student?.nameMissing ? '(name missing)' : confirm.student?.name ?? 'this student'}. Records remain in the database for compliance.`}
        confirmText="Delete student"
      />
    </>
  );
}

function fmtDate(d: string | null): string {
  return d ? dayjs(d).format('DD MMM YYYY') : '—';
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return (
    anyE?.response?.data?.error?.message ??
    anyE?.message ??
    'Something went wrong'
  );
}
